import { applyBehaviour, busyActAt, BUSY_CYCLE_SECONDS, type BuddyBehaviour } from './buddyBehaviours';
import { type BuddyEffect, type BuddyPose, freshPose } from './buddyEngine';
import { BUDDIES_A, BUDDIES_BUSY, BUDDIES_FIT_ORB, BUDDIES_PAIR, type BuddiesFit, type BuddiesWho } from './Buddies.constants';

export type BuddiesAct = BuddyBehaviour | typeof BUDDIES_BUSY;

export interface BuddiesScene {
  poses: BuddyPose[];
  effects: BuddyEffect[];
}

export interface BuddiesViewport {
  unit: number;
  groundY: number;
}

const ORB_DESIGN_SIZE = 150;
const ORB_BUDDY_SCALE = 1.35;
const ORB_SOLO_SCALE = 1.6;
const ORB_SPACING = 9;
const FILL_SPACING = 11;
const BUSY_PAIR_SPREAD = 1.4;

export const viewportOf = (size: number, who: BuddiesWho, fit: BuddiesFit): BuddiesViewport => {
  const solo = who !== BUDDIES_PAIR;
  if (fit === BUDDIES_FIT_ORB) {
    const k = size / ORB_DESIGN_SIZE;
    return { unit: k * ORB_BUDDY_SCALE * (solo ? ORB_SOLO_SCALE : 1), groundY: size / 2 + 14 * k };
  }
  return { unit: size / (solo ? 30 : 58), groundY: size * (solo ? 0.86 : 0.8) };
};

/** An undrawn partner on the same spot, so effects meant for it land on the buddy that is drawn. */
const standIn = (kind: 'A' | 'I', cx: number): BuddyPose => ({ ...freshPose(kind), cx });

/**
 * Busy pairs run the cycle half a loop apart, so they never do the same thing
 * at once; each one's pose comes from its own pass over the behaviour.
 */
export const sceneAt = (who: BuddiesWho, act: BuddiesAct, t: number, fit: BuddiesFit): BuddiesScene => {
  const A = freshPose('A');
  const I = freshPose('I');
  if (who !== BUDDIES_PAIR) {
    const solo = who === BUDDIES_A ? A : I;
    const { behaviour, t: local } = act === BUDDIES_BUSY ? busyActAt(t) : { behaviour: act, t };
    const effects = applyBehaviour(behaviour, A, I, local);
    return { poses: [solo], effects };
  }
  const spacing = fit === BUDDIES_FIT_ORB ? ORB_SPACING : FILL_SPACING;
  if (act !== BUDDIES_BUSY) {
    A.cx = -spacing;
    I.cx = spacing;
    return { poses: [A, I], effects: applyBehaviour(act, A, I, t) };
  }
  const spread = spacing * BUSY_PAIR_SPREAD;
  A.cx = -spread;
  I.cx = spread;
  const first = busyActAt(t);
  const second = busyActAt(t + BUSY_CYCLE_SECONDS / 2);
  const effects = [
    ...applyBehaviour(first.behaviour, A, standIn('I', A.cx), first.t),
    ...applyBehaviour(second.behaviour, standIn('A', I.cx), I, second.t),
  ];
  return { poses: [A, I], effects };
};
